import React, { useState } from 'react';
import { Modal, Typography, Button, Input, Form } from '@crisellevaldez/speculo-ui';
import { Alert as SpeculoAlert, AlertDescription } from '@crisellevaldez/speculo-ui';
import { DnsRecordCard } from './email/DnsRecordCard';
import { Organization } from '../../../types/organization.types';
import {
  useUpdateSender,
  useActivateOrganizationCampaigns,
  useDeactivateOrganizationCampaigns,
  useScheduleCampaign,
  useTestTemplate,
} from '../../../hooks/client/useEmailCampaigns';
import {
  useSetupOrganization,
  useValidateDns,
  useUpdateOrganization,
  useChangeDomain,
} from '../../../hooks/client/useSendgridDomainSetup';
import { getDnsRecords } from '../../../api/sendgridDomainSetup';
import { useToast } from '@crisellevaldez/speculo-ui';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Info, PenSquare, Shield } from 'lucide-react';
import { BulkEAlertSetup } from './email/BulkEAlertSetup';
import ConfirmationDialog from '../../common/dialogs/ConfirmationDialog';
import { LoadingDialog } from '../../common/dialogs/LoadingDialog';
import { EmailCampaignEAlertModal } from './email/EmailCampaignEAlertModal';
import { useUser } from '../../../hooks/useUser';
import { AdminLockOverlay } from './AdminLockOverlay';

const setupOrgSchema = z.object({
  email_domain: z
    .string()
    .min(1, 'Email domain is required')
    .regex(
      /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i,
      'Please enter a valid subdomain (e.g., mail.speculo.ai). Only letters, numbers, and hyphens are allowed.'
    )
    .transform((val) => val.toLowerCase()), // Convert to lowercase for consistency
  reply_to_email: z
    .string()
    .email('Please enter a valid email address')
    .transform((val) => val.toLowerCase()), // Convert to lowercase for consistency
  reply_to_name: z.string().min(1, 'Reply to name is required'),
});

type SetupOrgFormData = z.infer<typeof setupOrgSchema>;

interface EmailCampaignsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onSuccess?: () => void;
}

/**
 * Normalize DNS records from API responses into a keyed object.
 * Handles both array format (GET /dns-records) and object format (POST /setup-org).
 * Also unwraps the `data` wrapper if present.
 */
const normalizeDnsRecords = (
  raw: unknown
): Record<string, { data: string; host: string; type: string; propagated?: boolean }> | null => {
  if (!raw || typeof raw !== 'object') return null;

  // Unwrap `data` wrapper if present
  const unwrapped = 'data' in (raw as Record<string, unknown>)
    ? (raw as Record<string, unknown>).data
    : raw;

  if (!unwrapped || typeof unwrapped !== 'object') return null;

  // Try dns_records first, then dns_records_status (from validate-dns response)
  const uw = unwrapped as Record<string, unknown>;
  const records = uw.dns_records || uw.dns_records_status || unwrapped;

  if (!records) return null;

  // Array format: [{ name, host, type, value, propagated }]
  if (Array.isArray(records)) {
    const result: Record<string, { data: string; host: string; type: string; propagated?: boolean }> = {};
    for (const r of records) {
      const key = r.name || r.host;
      result[key] = { data: r.value || r.data, host: r.host, type: r.type, propagated: r.propagated };
    }
    return Object.keys(result).length > 0 ? result : null;
  }

  // Object format: { dkim1: { data, host, type } } or { dkim1: { hostname, expected, valid, message } }
  if (typeof records === 'object' && !Array.isArray(records)) {
    const obj = records as Record<string, Record<string, unknown>>;
    const keys = Object.keys(obj);
    if (keys.length === 0) return null;

    // Check if it's dns_records_status format (has `hostname` + `expected`)
    const first = obj[keys[0]];
    if (first && 'hostname' in first && 'expected' in first) {
      const result: Record<string, { data: string; host: string; type: string; propagated?: boolean }> = {};
      for (const [key, val] of Object.entries(obj)) {
        result[key] = {
          data: val.expected as string,
          host: val.hostname as string,
          type: 'cname',
          propagated: val.valid as boolean,
        };
      }
      return result;
    }

    return obj as unknown as Record<string, { data: string; host: string; type: string }>;
  }

  return null;
};

type Column = {
  key: string;
  header: string;
  cell?: (row: Record<string, unknown>) => React.ReactNode;
  width?: string;
  isCentered?: boolean;
};

export const EmailCampaignsModal: React.FC<EmailCampaignsModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
}) => {
  const { showToast } = useToast();
  const { isAdmin, isSuperAdmin } = useUser();
  const setupOrganization = useSetupOrganization();
  const validateDns = useValidateDns();
  const updateOrganization = useUpdateOrganization();
  const changeDomain = useChangeDomain();
  const _updateSender = useUpdateSender();
  const activateOrganization = useActivateOrganizationCampaigns();
  const deactivateOrganization = useDeactivateOrganizationCampaigns();
  const scheduleCampaign = useScheduleCampaign();
  const _testTemplate = useTestTemplate();

  const [_isTestModalOpen, _setIsTestModalOpen] = useState(false);
  const [justAuthenticated, setJustAuthenticated] = useState(false);
  const [showEnableConfirmation, setShowEnableConfirmation] = useState(false);
  const [showScheduleConfirmation, setShowScheduleConfirmation] = useState(false);
  const [showSchedulingDialog, setShowSchedulingDialog] = useState(false);
  const [showChangeDomainConfirmation, setShowChangeDomainConfirmation] = useState(false);
  const [isEAlertModalOpen, setIsEAlertModalOpen] = useState(false);
  const [eAlertInitialData, setEAlertInitialData] = useState<{ tag: string } | null>(null);
  const [isEAlertUpdateMode, setIsEAlertUpdateMode] = useState(false);
  const [newDomainData, setNewDomainData] = useState<{
    email_domain: string;
    reply_to_email: string;
    reply_to_name?: string;
  } | null>(null);
  const [setupDnsRecords, setSetupDnsRecords] = useState<Record<string, { data: string; host: string; type: string; valid?: boolean; propagated?: boolean }> | null>(null);
  const [isFetchingDns, setIsFetchingDns] = useState(false);
  const getErrorMessage = (error: unknown, fallback: string) =>
    error instanceof Error ? error.message : fallback;

  // Reset states when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setJustAuthenticated(false);
      setSetupDnsRecords(null);
    }
  }, [isOpen]);

  const form = useForm<SetupOrgFormData>({
    resolver: zodResolver(setupOrgSchema),
    defaultValues: {
      email_domain: selectedOrg?.domain || selectedOrg?.emailSetup?.custom_email_domain || '',
      reply_to_email: selectedOrg?.sendgrid_reply_to_email || selectedOrg?.reply_to_email || '',
      reply_to_name: selectedOrg?.sendgrid_reply_to_name || '',
    },
  });

  // Reset form when modal closes or org changes
  React.useEffect(() => {
    if (!isOpen) {
      form.reset();
    } else {
      form.reset({
        email_domain: selectedOrg?.emailSetup?.custom_email_domain || selectedOrg?.domain || '',
        reply_to_email: selectedOrg?.sendgrid_reply_to_email || selectedOrg?.reply_to_email || '',
        reply_to_name: selectedOrg?.sendgrid_reply_to_name || '',
      });
    }
  }, [selectedOrg, isOpen, form]);

  const handleSetupOrganization = async (data: SetupOrgFormData) => {
    if (!selectedOrg) return;

    try {
      const response = await setupOrganization.mutateAsync({
        organization_id: selectedOrg.id,
        email_domain: data.email_domain,
        reply_to_email: data.reply_to_email,
        reply_to_name: data.reply_to_name,
      });

      // Capture DNS records from setup response
      const normalized = normalizeDnsRecords(response);
      if (normalized) setSetupDnsRecords(normalized);

      setJustAuthenticated(true);

      showToast({
        message: 'Organization set up for email campaigns',
        variant: 'success',
      });

      // Reset form with the submitted value to keep it displayed
      form.reset({
        email_domain: data.email_domain,
        reply_to_email: data.reply_to_email,
        reply_to_name: data.reply_to_name,
      });

      // Notify parent component to refresh data
      onSuccess?.();
    } catch (error) {
      console.error('Error setting up organization:', error);
      showToast({
        message: getErrorMessage(error, 'Failed to set up organization'),
        variant: 'error',
      });
    }
  };

  const _columns: Column[] = [
    {
      key: 'name',
      header: 'Campaign Name',
      width: '250px',
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
    },
    {
      key: 'sent',
      header: 'Sent',
      width: '100px',
      isCentered: true,
    },
    {
      key: 'opened',
      header: 'Opened',
      width: '100px',
      isCentered: true,
    },
    {
      key: 'clicked',
      header: 'Clicked',
      width: '100px',
      isCentered: true,
    },
    {
      key: 'scheduled_for',
      header: 'Scheduled For',
      width: '200px',
    },
  ];

  // Placeholder empty data array until functionality is implemented
  const _campaigns: Record<string, unknown>[] = [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <Modal.Header>
        <div>
          <Typography variant="h3">Email Campaign</Typography>
          {/* <div className="text-sm text-gray-500">0 campaigns</div> */}
        </div>
      </Modal.Header>
      <Modal.Body className="py-8">
        <AdminLockOverlay locked={!isAdmin} subject="E-alert settings">
        <div className="space-y-6">
          {/* Only show Alert when email is set up OR super admin setting it up */}
          {(selectedOrg?.domain ||
            selectedOrg?.emailSetup?.custom_email_domain ||
            isSuperAdmin) && (
            <SpeculoAlert variant="info">
              <Info className="h-4 w-4" />
              <AlertDescription>
                {(selectedOrg?.domain || selectedOrg?.emailSetup?.custom_email_domain) &&
                selectedOrg?.dns_configured
                  ? 'Setup or Bulk Disable automated property alerts for contacts.'
                  : 'Please enter a subdomain for your email campaigns (e.g., mail.speculo.ai).'}
              </AlertDescription>
            </SpeculoAlert>
          )}

          {/* If email not set up and NOT super admin - show centered message */}
          {!(selectedOrg?.domain || selectedOrg?.emailSetup?.custom_email_domain) &&
          !isSuperAdmin ? (
            <div className="bg-white dark:bg-white/5 p-6 rounded-lg border border-gray-200 dark:border-white/15 flex items-center justify-center">
              <div className="text-center py-8">
                <h3 className="text-lg font-semibold mb-2">No Email Campaigns Available</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Email campaigns are not set up for your organization yet. Please contact your
                  administrator or our support team to enable this feature.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Organization Setup Form - Only show to super_admin */}
              {isSuperAdmin && (
                <div className="mb-4 bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
                  <form onSubmit={form.handleSubmit(handleSetupOrganization)} className="space-y-4">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-4">
                        <Form.Control>
                          <Form.Label required>Email Domain</Form.Label>
                          <div className="space-y-1">
                            <div className="flex gap-2">
                              <div className="flex-grow">
                                <Input
                                  {...form.register('email_domain')}
                                  placeholder="mail.speculo.ai"
                                  error={form.formState.errors.email_domain?.message}
                                  disabled={false}
                                  className="w-full"
                                />
                              </div>
                              {(selectedOrg?.domain ||
                                selectedOrg?.emailSetup?.custom_email_domain) && (
                                <Button
                                  type="button"
                                  size="md"
                                  variant="outline"
                                  className="flex items-center gap-1 h-9"
                                  isLoading={setupOrganization.isPending}
                                  disabled={
                                    setupOrganization.isPending ||
                                    (selectedOrg?.domain || '').trim().toLowerCase() ===
                                      form.watch('email_domain').trim().toLowerCase()
                                  }
                                  onClick={async () => {
                                    if (!selectedOrg) return;
                                    try {
                                      // Trigger validation for email_domain field
                                      const isValid = await form.trigger('email_domain');
                                      if (!isValid) {
                                        // Form will show validation error automatically
                                        return;
                                      }

                                      const email_domain = form.getValues('email_domain');
                                      const reply_to_email = form.getValues('reply_to_email');
                                      const reply_to_name = form.getValues('reply_to_name');

                                      // Store the data and show confirmation dialog
                                      setNewDomainData({
                                        email_domain,
                                        reply_to_email,
                                        reply_to_name,
                                      });
                                      setShowChangeDomainConfirmation(true);
                                    } catch (error) {
                                      showToast({
                                        message: 'Failed to change domain',
                                        variant: 'error',
                                      });
                                    }
                                  }}
                                >
                                  <PenSquare className="h-4 w-4 mr-1" /> Update Domain
                                </Button>
                              )}
                            </div>
                            {(selectedOrg?.domain ||
                              selectedOrg?.emailSetup?.custom_email_domain) && (
                              <Form.HelperText>
                                Sender: {selectedOrg.sendgrid_sender_email || ''}
                              </Form.HelperText>
                            )}
                          </div>
                        </Form.Control>

                        <Form.Control>
                          <div className="flex flex-col md:flex-row gap-2">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 flex-grow">
                              <div className="flex-grow">
                                <Form.Label required>Reply to Email</Form.Label>
                                <Input
                                  {...form.register('reply_to_email')}
                                  placeholder="Enter reply-to email address"
                                  error={form.formState.errors.reply_to_email?.message}
                                  className="w-full"
                                />
                              </div>
                              <div className="flex-grow">
                                <Form.Label required>Reply to Name</Form.Label>
                                <Input
                                  {...form.register('reply_to_name')}
                                  placeholder="Enter reply-to name"
                                  error={form.formState.errors.reply_to_name?.message}
                                  className="w-full"
                                />
                              </div>
                            </div>
                            {(selectedOrg?.domain ||
                              selectedOrg?.emailSetup?.custom_email_domain) && (
                              <Button
                                type="button"
                                size="md"
                                variant="outline"
                                className="flex items-center gap-1 h-9 md:self-end mt-6"
                                isLoading={updateOrganization.isPending}
                                disabled={
                                  updateOrganization.isPending ||
                                  (selectedOrg?.sendgrid_reply_to_email?.trim().toLowerCase() ===
                                    form.watch('reply_to_email').trim().toLowerCase() &&
                                    selectedOrg?.sendgrid_reply_to_name?.trim().toLowerCase() ===
                                      form.watch('reply_to_name').trim().toLowerCase())
                                }
                                onClick={async () => {
                                  if (!selectedOrg) return;
                                  try {
                                    // Trigger validation for reply_to_email and reply_to_name fields
                                    const isValid = await form.trigger([
                                      'reply_to_email',
                                      'reply_to_name',
                                    ]);
                                    if (!isValid) {
                                      // Form will show validation error automatically
                                      return;
                                    }

                                    const reply_to = form.getValues('reply_to_email').trim();
                                    const reply_to_name = form.getValues('reply_to_name').trim();

                                    // Check if values are the same as current values
                                    if (
                                      (selectedOrg.sendgrid_reply_to_email || '')
                                        .trim()
                                        .toLowerCase() === reply_to.toLowerCase() &&
                                      (selectedOrg.sendgrid_reply_to_name || '')
                                        .trim()
                                        .toLowerCase() === reply_to_name.toLowerCase()
                                    ) {
                                      return; // No need to update if values are the same
                                    }

                                    await updateOrganization.mutateAsync({
                                      organization_id: selectedOrg.id,
                                      reply_to_email: reply_to,
                                      reply_to_name: reply_to_name,
                                    });
                                    showToast({
                                      message: 'Sender update successful',
                                      variant: 'success',
                                    });
                                    onSuccess?.();
                                  } catch (error) {
                                    showToast({
                                      message: 'Failed to update sender',
                                      variant: 'error',
                                    });
                                  }
                                }}
                              >
                                <PenSquare className="h-4 w-4 mr-1" /> Update Sender
                              </Button>
                            )}
                          </div>
                        </Form.Control>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-2 justify-end">
                        {selectedOrg?.domain || selectedOrg?.emailSetup?.custom_email_domain || justAuthenticated ? (
                          <>
                            {!selectedOrg?.dns_configured && (
                              <>
                                <Button
                                  type="button"
                                  size="md"
                                  variant="outline"
                                  className="h-9 gap-2 py-2"
                                  isLoading={isFetchingDns}
                                  disabled={isFetchingDns || validateDns.isPending}
                                  onClick={async () => {
                                    if (!selectedOrg) return;
                                    setIsFetchingDns(true);
                                    try {
                                      const response = await getDnsRecords(selectedOrg.id);
                                      const normalized = normalizeDnsRecords(response);
                                      if (normalized) {
                                        setSetupDnsRecords(normalized);
                                        setJustAuthenticated(true);
                                      }
                                    } catch (error) {
                                      showToast({
                                        message: 'Failed to fetch DNS records',
                                        variant: 'error',
                                      });
                                    } finally {
                                      setIsFetchingDns(false);
                                    }
                                  }}
                                >
                                  Show DNS Records
                                </Button>
                                <Button
                                  type="button"
                                  size="md"
                                  variant="outline"
                                  className="h-9 gap-2 py-2 !bg-[#1c994a] !text-white"
                                  isLoading={validateDns.isPending}
                                  disabled={validateDns.isPending || isFetchingDns}
                                  onClick={async () => {
                                    if (!selectedOrg) return;
                                    try {
                                      const response = await validateDns.mutateAsync(selectedOrg.id);
                                      const result = response?.data || response;

                                      if (result.dns_configured) {
                                        setJustAuthenticated(false);
                                        setSetupDnsRecords(null);
                                        showToast({
                                          message: 'DNS records validated successfully',
                                          variant: 'success',
                                        });
                                      } else {
                                        const validatedRecords = normalizeDnsRecords(response);
                                        if (validatedRecords) {
                                          setSetupDnsRecords(validatedRecords);
                                        }
                                        setJustAuthenticated(true);
                                        showToast({
                                          message:
                                            result.validation_summary ||
                                            'Some DNS records are not correctly configured',
                                          variant: 'error',
                                        });
                                      }

                                      onSuccess?.();
                                    } catch (error) {
                                      showToast({
                                        message: 'Failed to validate DNS records',
                                        variant: 'error',
                                      });
                                    }
                                  }}
                                >
                                  Validate DNS <Shield className="h-4 w-4 mr-1" />
                                </Button>
                              </>
                            )}
                            {/* Test Email 
                    {selectedOrg?.dns_configured && (
                      <Button
                        type="button"
                        size="md"
                        variant="secondary"
                        disabled={false}
                        onClick={() => setIsTestModalOpen(true)}
                        className="py-2 h-9"
                      >
                        Test Email
                      </Button>
                    )}
                    */}
                          </>
                        ) : (
                          <Button
                            type="submit"
                            size="md"
                            variant="primary"
                            isLoading={setupOrganization.isPending}
                            disabled={setupOrganization.isPending || !selectedOrg}
                            className="py-2 h-9"
                          >
                            Set Up Organization
                          </Button>
                        )}
                      </div>
                    </div>

                    <div>
                      {(() => {
                        const dnsRecords = setupDnsRecords || selectedOrg?.required_dns_records;
                        return dnsRecords &&
                          Object.keys(dnsRecords).length > 0 &&
                          justAuthenticated ? (
                          <div className="mt-4 w-full space-y-1">
                            <Typography variant="small" className="font-semibold mb-3">
                              DNS Records
                            </Typography>
                            <div className="grid grid-cols-1 md:grid-cols-2 3xl:grid-cols-3 gap-3">
                              {Object.entries(dnsRecords).map(([key, record]) => (
                                <DnsRecordCard
                                  key={key}
                                  name={key}
                                  record={record as { data: string; host: string; type: string }}
                                />
                              ))}
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                    <div className="flex justify-end"></div>
                  </form>
                </div>
              )}

              {/* Property eAlerts Section - Show to all users if email is set up and DNS is configured */}
              {(selectedOrg?.domain || selectedOrg?.emailSetup?.custom_email_domain) &&
                selectedOrg?.dns_configured && (
                  <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                      <div>
                        <Typography variant="h3" className="text-lg font-semibold">
                          Property eAlerts
                        </Typography>
                        <Typography variant="small" className="text-gray-500 dark:text-gray-400">
                          Setup or Bulk Disable automated property alerts for contacts
                        </Typography>
                      </div>
                    </div>

                    <BulkEAlertSetup
                      selectedOrg={selectedOrg}
                      onSuccess={onSuccess}
                      enabled={isOpen}
                      onOpenAdvancedSetup={(data, isUpdate = false) => {
                        console.log('BulkEAlertSetup data:', data);
                        setEAlertInitialData(data);
                        setIsEAlertUpdateMode(isUpdate);
                        setIsEAlertModalOpen(true);
                      }}
                    />
                  </div>
                )}

              {/* Campaign Management Section - Deprecated (only shown to super_admin for reference)
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-300">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                  <div>
                    <Typography variant="h3" className="text-lg font-semibold">
                      Campaign Management
                    </Typography>
                    <Typography variant="small" className="text-gray-500">
                      Activate and schedule your email campaigns
                    </Typography>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 items-center">
                    <div className="mr-2">
                      <Typography variant="small" className="font-medium">
                        Status:{' '}
                        <span
                          className={
                            selectedOrg?.emailCampaignsEnabled
                              ? 'text-green-600 font-semibold'
                              : 'text-red-600 font-semibold'
                          }
                        >
                          {selectedOrg?.emailCampaignsEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </Typography>
                    </div>
                    <Button
                      type="button"
                      size="md"
                      variant="secondary"
                      isLoading={activateOrganization.isPending || deactivateOrganization.isPending}
                      disabled={
                        activateOrganization.isPending ||
                        deactivateOrganization.isPending ||
                        (!selectedOrg?.emailCampaignsEnabled && !selectedOrg?.dns_configured)
                      }
                      onClick={() => setShowEnableConfirmation(true)}
                      className={`py-2 ${
                        selectedOrg?.emailCampaignsEnabled && selectedOrg?.dns_configured
                          ? '!bg-red-600 !text-white'
                          : '!bg-green-600 !text-white'
                      }`}
                    >
                      {selectedOrg?.emailCampaignsEnabled && selectedOrg?.dns_configured
                        ? 'Disable Campaign'
                        : 'Enable Campaign'}
                    </Button>
                  </div>
                </div>

                <BulkEnableContacts
                  selectedOrg={selectedOrg}
                  onSuccess={onSuccess}
                  enabled={isOpen}
                />
              </div>
              */}
            </>
          )}
        </div>
        </AdminLockOverlay>
      </Modal.Body>

      {/* Footer with Cancel button */}
      <Modal.Footer>
        <div className="flex gap-2 w-full md:w-auto ml-auto justify-end">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>

      {/* Test Email Modal - Commented out as it's not updated
      <TestTemplateModal
        isOpen={isTestModalOpen}
        onClose={() => setIsTestModalOpen(false)}
        organizationId={selectedOrg?.id || ''}
        onTest={async (data) => {
          if (!selectedOrg) return;
          await testTemplate.mutateAsync({
            organization_id: selectedOrg.id,
            template_id: 'home_value_1',
            contact_email: data.contact_email,
            contact_name: data.contact_name,
          });
          showToast({
            message: 'Test email sent successfully',
            variant: 'success',
          });
        }}
        isLoading={testTemplate.isPending}
      />
      */}

      <ConfirmationDialog
        isOpen={showEnableConfirmation}
        onClose={() => setShowEnableConfirmation(false)}
        title={
          selectedOrg?.emailCampaignsEnabled ? 'Disable Email Campaigns' : 'Enable Email Campaigns'
        }
        message={
          selectedOrg?.emailCampaignsEnabled
            ? 'Are you sure you want to disable email campaigns for this organization?'
            : 'Are you sure you want to enable email campaigns for this organization?'
        }
        confirmLabel={selectedOrg?.emailCampaignsEnabled ? 'Disable' : 'Enable'}
        variant="warning"
        isLoading={activateOrganization.isPending || deactivateOrganization.isPending}
        onConfirm={async () => {
          if (!selectedOrg) return;
          try {
            if (selectedOrg.emailCampaignsEnabled) {
              // Disable campaigns
              await deactivateOrganization.mutateAsync(selectedOrg.id);
              showToast({
                message: 'Campaign deactivated successfully.',
                variant: 'success',
              });
            } else {
              // Enable campaigns
              await activateOrganization.mutateAsync(selectedOrg.id);
              showToast({
                message: 'Campaign activated successfully. You can now schedule your campaign.',
                variant: 'success',
              });
            }
            onSuccess?.();
            setShowEnableConfirmation(false);
          } catch (error) {
            showToast({
              message: selectedOrg.emailCampaignsEnabled
                ? 'Failed to deactivate campaign system'
                : 'Failed to activate campaign system',
              variant: 'error',
            });
          }
        }}
      />

      <ConfirmationDialog
        isOpen={showScheduleConfirmation}
        onClose={() => setShowScheduleConfirmation(false)}
        title="Schedule Campaigns"
        message="Are you sure you want to schedule email campaigns for this organization?"
        confirmLabel="Schedule"
        variant="warning"
        isLoading={scheduleCampaign.isPending}
        onConfirm={async () => {
          if (!selectedOrg) return;
          setShowScheduleConfirmation(false);
          setShowSchedulingDialog(true);
          try {
            await scheduleCampaign.mutateAsync(selectedOrg.id);
            showToast({
              message: 'Campaigns scheduled successfully',
              variant: 'success',
            });
            onSuccess?.();
          } catch (error) {
            showToast({
              message: 'Failed to schedule campaigns',
              variant: 'error',
            });
          } finally {
            setShowSchedulingDialog(false);
          }
        }}
      />
      <LoadingDialog isOpen={showSchedulingDialog} message="Scheduling campaign..." />

      {/* Change Domain Confirmation */}
      <ConfirmationDialog
        isOpen={showChangeDomainConfirmation}
        onClose={() => setShowChangeDomainConfirmation(false)}
        title="Change Email Domain"
        message={`Are you sure you want to change the domain to ${newDomainData?.email_domain}? This will require re-validating DNS records.`}
        confirmLabel="Change Domain"
        variant="warning"
        isLoading={changeDomain.isPending}
        onConfirm={async () => {
          if (!selectedOrg || !newDomainData) return;

          try {
            const response = await changeDomain.mutateAsync({
              organization_id: selectedOrg.id,
              email_domain: newDomainData.email_domain,
            });

            const domainRecords = normalizeDnsRecords(response);
            if (domainRecords) setSetupDnsRecords(domainRecords);

            setJustAuthenticated(true);
            showToast({
              message: 'Domain changed successfully. Please validate the new DNS records.',
              variant: 'success',
            });
            onSuccess?.();
            setShowChangeDomainConfirmation(false);
          } catch (error) {
            showToast({
              message: getErrorMessage(error, 'Failed to change domain'),
              variant: 'error',
            });
          }
        }}
      />

      {/* eAlert Modal */}
      <EmailCampaignEAlertModal
        isOpen={isEAlertModalOpen}
        onClose={() => {
          setIsEAlertModalOpen(false);
          setEAlertInitialData(null);
          setIsEAlertUpdateMode(false);
        }}
        organization={selectedOrg}
        initialData={eAlertInitialData ?? undefined}
        isUpdateMode={isEAlertUpdateMode}
      />
    </Modal>
  );
};
